import { useState, useEffect } from 'react'
import { FileText, Download, CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import { Sale, salesService } from '../../../services/sales.service'
import { facturamaService } from '../../../services/facturama.service'

interface InvoiceData {
  rfc: string
  name: string
  cfdi_use: string
  fiscal_regime: string
  zip_code: string
  email: string
}

interface InvoiceSaleModalProps {
  isOpen: boolean
  onClose: () => void
  sale: Sale | null
}

export function InvoiceSaleModal({ isOpen, onClose, sale }: InvoiceSaleModalProps) {
  const queryClient = useQueryClient()

  const { data: facturamaProfile } = useQuery({
    queryKey: ['facturama-profile'],
    queryFn: facturamaService.getProfile,
    staleTime: 1000 * 60 * 60 * 2, // 2 horas
  })

  const [invoiceData, setInvoiceData] = useState<InvoiceData>({
    rfc: '',
    name: '',
    cfdi_use: '',
    fiscal_regime: '',
    zip_code: '',
    email: '',
  })

  useEffect(() => {
    if (isOpen && sale) {
      if (sale.customer_data?.id) {
        // Tiene un cliente registrado asociado
        setInvoiceData({
          rfc: sale.customer_data.rfc || '',
          name: sale.customer_data.name || '',
          cfdi_use: sale.customer_data.cfdi_use || '',
          fiscal_regime: sale.customer_data.fiscal_regime || '',
          zip_code: sale.customer_data.zip_code || '',
          email: sale.customer_data.email || '',
        })
      } else {
        // Es venta a mostrador, llenar con Público en General
        setInvoiceData({
          rfc: 'XAXX010101000',
          name: 'PUBLICO EN GENERAL',
          cfdi_use: 'S01',
          fiscal_regime: '616',
          zip_code: facturamaProfile?.IssuedIn?.ZipCode || facturamaProfile?.TaxAddress?.ZipCode || '',
          email: '',
        })
      }
    } else if (!isOpen) {
      // Limpiar al cerrar
      setInvoiceData({
        rfc: '',
        name: '',
        cfdi_use: '',
        fiscal_regime: '',
        zip_code: '',
        email: '',
      })
    }
  }, [isOpen, sale, facturamaProfile])

  const isPublicoGeneral = invoiceData.rfc === 'XAXX010101000'
  const isComplete =
    invoiceData.rfc &&
    invoiceData.name &&
    invoiceData.cfdi_use &&
    invoiceData.fiscal_regime &&
    invoiceData.zip_code

  const invoiceMutation = useMutation({
    mutationFn: () =>
      salesService.invoiceSale(sale!.id, {
        rfc: invoiceData.rfc,
        name: invoiceData.name,
        cfdi_use: invoiceData.cfdi_use,
        fiscal_regime: invoiceData.fiscal_regime,
        zip_code: invoiceData.zip_code,
        email: invoiceData.email || undefined,
      }),
    onSuccess: () => {
      toast.success('Factura generada correctamente')
      queryClient.invalidateQueries({ queryKey: ['sales'] })
      queryClient.invalidateQueries({ queryKey: ['saleDetail', sale?.id] })
      onClose()
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Error al generar la factura')
    },
  })

  if (!isOpen || !sale) return null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Datos de Facturación
          </DialogTitle>
          <DialogDescription>
            {isPublicoGeneral
              ? 'Datos predeterminados para facturación a Público en General.'
              : 'Ingrese los datos fiscales del cliente para generar la factura.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-card-foreground">RFC</label>
              {isPublicoGeneral ? (
                <div className="p-2 bg-card rounded-md border border-border text-sm font-medium text-card-foreground">
                  {invoiceData.rfc}
                </div>
              ) : (
                <Input
                  value={invoiceData.rfc}
                  onChange={(e) =>
                    setInvoiceData({ ...invoiceData, rfc: e.target.value.toUpperCase() })
                  }
                  placeholder="XAXX010101000"
                  maxLength={13}
                />
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-card-foreground">Código Postal</label>
              {isPublicoGeneral ? (
                <div className="flex h-[38px] items-center rounded-md border border-border bg-card px-3 text-sm font-medium text-card-foreground">
                  {invoiceData.zip_code}
                </div>
              ) : (
                <Input
                  value={invoiceData.zip_code}
                  onChange={(e) =>
                    setInvoiceData({ ...invoiceData, zip_code: e.target.value })
                  }
                  placeholder="Ej. 06600"
                  maxLength={5}
                />
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-card-foreground">Razón Social</label>
            {isPublicoGeneral ? (
              <div className="p-2 bg-card rounded-md border border-border text-sm font-medium text-card-foreground">
                {invoiceData.name}
              </div>
            ) : (
              <Input
                value={invoiceData.name}
                onChange={(e) =>
                  setInvoiceData({ ...invoiceData, name: e.target.value.toUpperCase() })
                }
                placeholder="Razón Social del receptor"
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-card-foreground">Régimen Fiscal</label>
              {isPublicoGeneral ? (
                <div className="p-2 bg-card rounded-md border border-border text-sm font-medium text-card-foreground">
                  Sin obligaciones fiscales (616)
                </div>
              ) : (
                <Select
                  value={invoiceData.fiscal_regime}
                  onValueChange={(val) =>
                    setInvoiceData({ ...invoiceData, fiscal_regime: val })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="616">Sin obligaciones fiscales (616)</SelectItem>
                    <SelectItem value="601">General de Ley Personas Morales (601)</SelectItem>
                    <SelectItem value="605">Sueldos y Salarios (605)</SelectItem>
                    <SelectItem value="606">Arrendamiento (606)</SelectItem>
                    <SelectItem value="612">Personas Físicas con Actividades Emp (612)</SelectItem>
                    <SelectItem value="621">Incorporación Fiscal (621)</SelectItem>
                    <SelectItem value="626">RESICO (626)</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-card-foreground">Uso de CFDI</label>
              {isPublicoGeneral ? (
                <div className="p-2 bg-card rounded-md border border-border text-sm font-medium text-card-foreground">
                  Sin efectos fiscales (S01)
                </div>
              ) : (
                <Select
                  value={invoiceData.cfdi_use}
                  onValueChange={(val) =>
                    setInvoiceData({ ...invoiceData, cfdi_use: val })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="S01">Sin efectos fiscales (S01)</SelectItem>
                    <SelectItem value="G01">Adquisición de mercancías (G01)</SelectItem>
                    <SelectItem value="G03">Gastos en general (G03)</SelectItem>
                    <SelectItem value="D01">
                      Honorarios médicos, dentales y gastos hospitalarios (D01)
                    </SelectItem>
                    <SelectItem value="CP01">Pagos (CP01)</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Email opcional */}
          <div className="mt-4 space-y-2 rounded-lg border border-primary/20 bg-card p-3">
            <label className="text-sm font-medium text-card-foreground">
              Correo Electrónico (Opcional)
            </label>
            <Input
              value={invoiceData.email}
              onChange={(e) =>
                setInvoiceData({ ...invoiceData, email: e.target.value })
              }
              placeholder="cliente@ejemplo.com"
              type="email"
              className="border-border bg-card text-card-foreground focus-visible:ring-primary"
            />
            <p className="text-xs text-card-description">
              Se enviará automáticamente el PDF y XML de la factura a esta dirección.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={invoiceMutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => invoiceMutation.mutate()}
            disabled={!isComplete || invoiceMutation.isPending}
          >
            {invoiceMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generando...
              </>
            ) : (
              <>
                <FileText className="mr-2 h-4 w-4" />
                Generar Factura
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─────────────────────────────────────────────
// Modal para descargar factura (PDF / XML)
// ─────────────────────────────────────────────

interface DownloadInvoiceModalProps {
  isOpen: boolean
  onClose: () => void
  sale: Sale | null
}

export function DownloadInvoiceModal({ isOpen, onClose, sale }: DownloadInvoiceModalProps) {
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [downloadingXml, setDownloadingXml] = useState(false)
  const [downloadingReturnPdf, setDownloadingReturnPdf] = useState(false)
  const [downloadingReturnXml, setDownloadingReturnXml] = useState(false)

  if (!isOpen || !sale) return null

  const cfdiId = sale.cfdi_id
  const returnCfdiId = sale.return_summary?.latest_return_cfdi_id

  const handleDownloadPdf = async () => {
    if (!cfdiId) return
    setDownloadingPdf(true)
    try {
      toast.info('Descargando PDF...')
      const base64 = await facturamaService.downloadInvoicePdf(cfdiId)
      const link = document.createElement('a')
      link.href = `data:application/pdf;base64,${base64}`
      link.download = `Factura_${sale.ticket_number || sale.id}.pdf`
      link.click()
      toast.success('PDF descargado correctamente')
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error al descargar el PDF')
    } finally {
      setDownloadingPdf(false)
    }
  }

  const handleDownloadXml = async () => {
    if (!cfdiId) return
    setDownloadingXml(true)
    try {
      toast.info('Descargando XML...')
      const base64 = await facturamaService.downloadInvoiceXml(cfdiId)
      const link = document.createElement('a')
      link.href = `data:application/xml;base64,${base64}`
      link.download = `Factura_${sale.ticket_number || sale.id}.xml`
      link.click()
      toast.success('XML descargado correctamente')
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error al descargar el XML')
    } finally {
      setDownloadingXml(false)
    }
  }

  const handleDownloadReturnPdf = async () => {
    if (!returnCfdiId) return
    setDownloadingReturnPdf(true)
    try {
      toast.info('Descargando PDF de Nota de Crédito...')
      const base64 = await facturamaService.downloadInvoicePdf(returnCfdiId)
      const link = document.createElement('a')
      link.href = `data:application/pdf;base64,${base64}`
      link.download = `NotaCredito_${sale.ticket_number || sale.id}.pdf`
      link.click()
      toast.success('PDF descargado correctamente')
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error al descargar el PDF')
    } finally {
      setDownloadingReturnPdf(false)
    }
  }

  const handleDownloadReturnXml = async () => {
    if (!returnCfdiId) return
    setDownloadingReturnXml(true)
    try {
      toast.info('Descargando XML de Nota de Crédito...')
      const base64 = await facturamaService.downloadInvoiceXml(returnCfdiId)
      const link = document.createElement('a')
      link.href = `data:application/xml;base64,${base64}`
      link.download = `NotaCredito_${sale.ticket_number || sale.id}.xml`
      link.click()
      toast.success('XML descargado correctamente')
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error al descargar el XML')
    } finally {
      setDownloadingReturnXml(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-primary" />
            Descargar Factura
          </DialogTitle>
          <DialogDescription>
            Descarga el comprobante fiscal de la venta{' '}
            <span className="font-medium text-slate-700">
              {sale.ticket_number || `#${sale.id}`}
            </span>
            .
          </DialogDescription>
        </DialogHeader>

        {cfdiId ? (
          <div className="space-y-3 py-2">
            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
              <p className="text-sm text-green-800">
                Factura generada · CFDI ID:{' '}
                <span className="font-mono font-medium">{cfdiId}</span>
              </p>
            </div>

            <Button
              className="w-full"
              variant="outline"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              {downloadingPdf ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileText className="mr-2 h-4 w-4 text-red-500" />
              )}
              Descargar PDF
            </Button>

            <Button
              className="w-full"
              variant="outline"
              onClick={handleDownloadXml}
              disabled={downloadingXml}
            >
              {downloadingXml ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileText className="mr-2 h-4 w-4 text-blue-500" />
              )}
              Descargar XML
            </Button>

            {returnCfdiId && (
              <>
                <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 mt-4 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-orange-600 shrink-0" />
                  <p className="text-sm text-orange-800">
                    Nota de Crédito generada · CFDI ID:{' '}
                    <span className="font-mono font-medium">{returnCfdiId}</span>
                  </p>
                </div>
                <Button
                  className="w-full border-orange-200 text-orange-700 hover:bg-orange-50 hover:text-orange-800"
                  variant="outline"
                  onClick={handleDownloadReturnPdf}
                  disabled={downloadingReturnPdf}
                >
                  {downloadingReturnPdf ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="mr-2 h-4 w-4 text-orange-600" />
                  )}
                  Descargar PDF (Nota de Crédito)
                </Button>
                <Button
                  className="w-full border-orange-200 text-orange-700 hover:bg-orange-50 hover:text-orange-800"
                  variant="outline"
                  onClick={handleDownloadReturnXml}
                  disabled={downloadingReturnXml}
                >
                  {downloadingReturnXml ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="mr-2 h-4 w-4 text-orange-600" />
                  )}
                  Descargar XML (Nota de Crédito)
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3">
            <XCircle className="h-4 w-4 text-yellow-600 shrink-0" />
            <p className="text-sm text-yellow-800">
              Esta venta está marcada como facturada pero no se encontró el ID del CFDI.
              Contacta a soporte.
            </p>
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
